<template>
  <div
    id="navigation"
    class="navigation"
    :class="navigationClass"
    :style="navigationMinHeight"
    v-scroll="handleScroll"
    v-resize="handleResize"
  >
    <div class="navigation__inner" id="navigation__inner">
      <div class="container">
        <breadcrumbs :items="breadcrumbsItems" />
        <breadcrumbs breadcrumbsClass="breadcrumbs_mobile" :items="breadcrumbsItemsMobile" :singleItem="true" />
        <h1 v-if="isDownloadsCategory" class="main-title navigation__title">
          <template v-if="$route.params.product">
            <LockIcon :published-to="productPublishedTo" />
            {{ productName }}
          </template>
          <template v-else-if="$route.params.subcategoryTab">
            <LockIcon :published-to="subcategory.publishedTo" />
            {{ subcategoryTabName }}
          </template>
          <template v-else>
            <LockIcon :published-to="subcategory.publishedTo" />
            {{ setProperFieldExternalFirst(subcategory, 'title') }}
          </template>
        </h1>
        <h1 v-else class="main-title navigation__title">
          <template v-if="$route.params.product">
            <LockIcon :published-to="productPublishedTo" />
            {{ productName }}
          </template>
          <template v-else>
            <LockIcon :published-to="subcategory.publishedTo" />
            {{ setProperFieldExternalFirst(subcategory, 'title') }}
          </template>
        </h1>
        <tabs
          v-if="isProductsCategory && subcategory && subcategory.nodes && subcategory.nodes.length && !$route.params.product"
          :items="subcategory.nodes"
          :core-path="corePath"
        ></tabs>
      </div>
    </div>
  </div>
</template>

<script>
  import Breadcrumbs from '~/components/ui/Breadcrumbs.vue';
  import Tabs from '~/components/ui/Tabs.vue';
  import { useMainStore } from '@/stores/main';
  import { storeToRefs } from 'pinia';

  export default {
    components: {
      Breadcrumbs,
      Tabs
    },
    props: {
      subcategory: {
        default() {
          return {};
        },
        type: Object
      },
      categoryName: {
        default: '',
        type: String
      }
    },
    data() {
      return {
        navigationClass: '',
        lastScrollTop: 0,
        offsetToMenuTitle: 0,
        navigationHeight: 0,
        productName: '',
        subcategoryTabName: ''
      };
    },
    setup() {
      const mainStore = useMainStore();
      const { langJson, lang, subcategoryTabData, mainCategories } = storeToRefs(mainStore);
      const { $eventBus: eventBus } = useNuxtApp();
      return { mainStore, langJson, lang, subcategoryTabData, mainCategories, eventBus };
    },
    computed: {
      isProductsCategory() {
        return this.mainCategory == this.mainCategories.products;
      },
      isDownloadsCategory() {
        return this.mainCategory == this.mainCategories.downloads;
      },
      mainCategory() {
        return '/' + this.$route.path.split('/')[1];
      },
      corePath() {
        return `${this.mainCategory}/${this.$route.params.category}/${this.$route.params.subcategory}/`;
      },
      breadcrumbsItems() {
        // Build deterministically in a fixed order to avoid SSR/CSR key/text mismatches
        const items = [{ name: this.langJson[this.lang].allProducts, link: '/' }];
        const routeParams = this.$route.params || {};
        let path = this.mainCategory;
        let name = '';
        const orderedKeys = ['category', 'subcategory', 'subcategoryTab', 'product'];

        for (const key of orderedKeys) {
          if (key === 'channelNodes' || key === 'token') continue;
          const val = routeParams[key];
          if (val === undefined) continue;
          path += '/' + val;
          if (key === 'category') {
            name = this.categoryName;
          } else if (key === 'subcategory') {
            name =
              this.subcategory && this.subcategory.external && this.subcategory.external.title
                ? this.subcategory.external.title
                : this.subcategory && this.subcategory.title
                  ? this.subcategory.title
                  : '';
          } else if (key === 'subcategoryTab') {
            if (this.subcategory) {
              const tabIndex = this.findNodeIdIndexInArray(this.subcategory.nodes, val);
              if (tabIndex !== -1 && this.subcategory.nodes[tabIndex]?.external?.title) {
                name = this.subcategory.nodes[tabIndex].external.title;
              }
              this.subcategoryTabName = name;
            }
          } else if (key === 'product') {
            const productIndex = this.findIdIndexInArray(this.subcategoryTabData, val);
            if (productIndex !== -1 && this.subcategoryTabData[productIndex]?.external?.title) {
              name = this.subcategoryTabData[productIndex].external.title;
            }
            this.productName = name;
          }
          items.push({ name, type: key, link: path + '/' });
        }
        this.mainStore.setBreadcrumbs(items);
        return items;
      },
      breadcrumbsItemsMobile() {
        const routeParams = this.$route.params;
        if (routeParams.hasOwnProperty('product')) {
          return [this.breadcrumbsItems.find((item) => item.type === 'subcategoryTab')];
        }
        if (routeParams.hasOwnProperty('subcategoryTab')) {
          return [this.breadcrumbsItems.find((item) => item.type === 'category')];
        }
        if (routeParams.hasOwnProperty('subcategory')) {
          return [this.breadcrumbsItems.find((item) => item.type === 'category')];
        }
      },
      navigationMinHeight() {
        // to prevent content jump when navigation becomes sticky
        return 'min-height:' + this.navigationHeight + 'px';
      },
      // Privacy state of the current product — drives the heading lock on the
      // product detail page. Resolved from subcategoryTabData like productName.
      productPublishedTo() {
        const productIndex = this.findIdIndexInArray(this.subcategoryTabData, this.$route.params.product);
        return productIndex !== -1 ? this.subcategoryTabData[productIndex]?.publishedTo : undefined;
      }
    },
    methods: {
      getOffsetToMenuTitle() {
        if (typeof document === 'undefined') return;

        this.offsetToMenuTitle = 0;
        const headerEl = document.getElementById('header');
        if (headerEl) {
          this.offsetToMenuTitle += headerEl.clientHeight;
        }
        const bannerEl = document.getElementById('banner');
        if (bannerEl) {
          this.offsetToMenuTitle += bannerEl.clientHeight;
        }
        const breadcrumbsEl = document.getElementById('breadcrumbs');
        if (breadcrumbsEl) {
          this.offsetToMenuTitle += breadcrumbsEl.clientHeight;
        }
      },
      getNavigationHeight() {
        if (typeof document === 'undefined') return;

        const navigationInnerEl = document.getElementById('navigation__inner');
        if (navigationInnerEl) {
          this.navigationHeight = navigationInnerEl.clientHeight;
        }
      },
      handleResize() {
        this.getOffsetToMenuTitle();
        this.getNavigationHeight();
      },
      handleScroll(event, el) {
        if (typeof window === 'undefined') return;

        if ((window.scrollY || window.pageYOffset) > this.offsetToMenuTitle) {
          // fix for IE11 START
          if (this.lastScrollTop == (window.scrollY || window.pageYOffset)) return;
          // fix END
          this.navigationClass = 'navigation_sticky';
          if ((window.scrollY || window.pageYOffset) > this.lastScrollTop) {
            this.navigationClass = 'navigation_sticky';
          } else {
            this.navigationClass = 'navigation_sticky navigation_sticky-show';
          }
          this.lastScrollTop = window.scrollY || window.pageYOffset;
        } else {
          this.navigationClass = '';
        }
      },
      findNodeIdIndexInArray(arr, nodeId) {
        if (!arr) return -1;
        return arr.findIndex((item) => item.nodeId === nodeId);
      },
      findIdIndexInArray(arr, id) {
        if (!arr) return -1;
        return arr.findIndex((item) => item.id === id);
      }
    },
    mounted() {
      this.getOffsetToMenuTitle();
      this.getNavigationHeight();
      // Use event bus for cross-component events
      this.eventBus.on('check-navigation-parameters', this.handleResize);
      this.eventBus.on('banner-mounted', this.handleResize);
      this.eventBus.on('banner-destroyed', this.handleResize);
    },
    unmounted() {
      // Clean up event listeners
      this.eventBus.off('check-navigation-parameters', this.handleResize);
      this.eventBus.off('banner-mounted', this.handleResize);
      this.eventBus.off('banner-destroyed', this.handleResize);
    }
  };
</script>
